import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldAlert, Crown } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { api, formatApiError } from "@/lib/api";

const ACTIONS = [["remove", "Remove content"], ["warn", "Warn user"], ["ban", "Ban user"], ["dismiss", "Dismiss"]];

export const SafetyPanel = () => {
  const [list, setList] = useState(null);
  const load = () => api.get("/admin/reports").then((r) => setList(Array.isArray(r.data) ? r.data : [])).catch(() => setList([]));
  useEffect(() => { load(); }, []);
  const act = async (rp, action) => {
    if (action === "ban" && !window.confirm(`Ban ${rp.target_name}? They won't be able to use Daybreyk.`)) return;
    try { await api.post(`/admin/reports/${rp.report_id}`, { action }); toast.success("Done"); load(); } catch (e) { toast.error(formatApiError(e)); }
  };
  if (!list) return null;
  const open = list.filter((r) => r.status === "open");
  return (
    <section data-testid="admin-safety" className="card p-5 sm:p-6">
      <p className="eyebrow flex items-center gap-2 !text-rose-700"><ShieldAlert className="w-4 h-4" />Safety</p>
      <h2 className="mt-1 font-display text-3xl text-slate-900">Reports · {open.length} open</h2>
      <ul className="mt-4 space-y-2 max-h-[28rem] overflow-y-auto">
        {list.slice(0, 60).map((rp) => (
          <li key={rp.report_id} data-testid={`report-row-${rp.report_id}`} className={`rounded-2xl border p-3 ${rp.status === "open" ? "border-brand/50 bg-brand/5" : "border-slate-200 opacity-60"}`}>
            <p className="text-xs text-slate-500"><b className="text-slate-900 text-sm">{rp.kind === "wellbeing" ? "Wellbeing alert" : rp.auto ? "Auto-flagged user" : `Reported ${rp.kind}`}</b> · {rp.target_name} · {new Date(rp.ts).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" })}{rp.reporter_name && ` · by ${rp.reporter_name}`}{rp.action && ` · ${rp.action}`}</p>
            {rp.excerpt && <p className="mt-1 text-sm text-slate-800">"{rp.excerpt}"</p>}
            {rp.reason && <p className="mt-0.5 text-xs text-slate-600">Reason: {rp.reason}</p>}
            {rp.status === "open" && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {rp.kind === "wellbeing" && <p className="w-full text-xs text-slate-600">They were shown Childline and Shout details. Please check in with them.</p>}
                {(rp.kind === "wellbeing" ? [["dismiss", "Mark as handled"]] : ACTIONS.filter(([k]) => !(k === "remove" && rp.auto))).map(([k, l]) => (
                  <button key={k} data-testid={`report-${k}-${rp.report_id}`} onClick={() => act(rp, k)} className={`rounded-full px-3 py-1 text-xs font-semibold ${k === "ban" ? "bg-brand text-white" : "border border-slate-300 text-slate-700 hover:border-gold-400"}`}>{l}</button>
                ))}
              </div>
            )}
          </li>
        ))}
        {!list.length && <li className="text-sm text-slate-500">No reports. All calm.</li>}
      </ul>
    </section>
  );
};

export const PremiumToggle = ({ u }) => {
  const [on, setOn] = useState(Boolean(u.premium_granted));
  const set = async (v) => { try { await api.put(`/admin/users/${u.user_id}/premium`, { grant: v }); setOn(v); toast.success(v ? "Free Premium given" : "Free Premium removed"); } catch (e) { toast.error(formatApiError(e)); } };
  return (
    <label data-testid="admin-premium-toggle" className="mt-4 flex items-center gap-3 rounded-xl border border-gold-400/50 bg-gold-400/10 px-3 py-2.5 text-sm text-slate-800">
      <Crown className="w-4 h-4 text-gold-300" /><span className="flex-1">Free Premium{u.premium_until ? ` · paid until ${u.premium_until.slice(0, 10)}` : ""}</span>
      <Switch data-testid="admin-premium-switch" checked={on} onCheckedChange={set} />
    </label>
  );
};
